/**
 * Module 118 (WP-118.2) — quest progress, pure.
 *
 * Ten permanent goals (`QUESTS`, frozen at WP-118.1), each paid once ever.
 * Progress moves only on events the plugin already has, never on a per-move
 * event (the Module 116 lesson). Progress is capped at the target, and once
 * a quest is complete nothing about it changes again, so the number of saves
 * this can cause is bounded by the targets.
 */
import { QUESTS, QUEST_MOVES_SPARE } from '../constants/economy';
export const EMPTY_QUESTS = { progress: {}, claimed: [], run: 0 };
/** The value each quest's progress should move to for this event, or undefined. */
function nextValue(q, current, evt, run) {
    switch (q.id) {
        case 'win_5':
        case 'win_50':
            return evt.type === 'win' ? current + 1 : undefined;
        case 'win_moves_spare':
            return evt.type === 'win' && evt.movesLeft >= QUEST_MOVES_SPARE ? current + 1 : undefined;
        case 'win_streak_3':
            return evt.type === 'win' ? Math.max(current, run) : undefined;
        case 'powered_gate_3':
            return evt.type === 'gatesPowered' && evt.count > 0 ? current + evt.count : undefined;
        case 'use_powerup':
            return evt.type === 'powerupUsed' ? current + 1 : undefined;
        case 'daily_finish':
            return evt.type === 'dailyCleared' ? current + 1 : undefined;
        case 'daily_streak_3':
            return evt.type === 'dailyCleared' ? Math.max(current, evt.streak) : undefined;
        case 'reach_puzzle_20':
            return evt.type === 'puzzleStarted' ? Math.max(current, evt.puzzle) : undefined;
        case 'play_intermediate':
            return evt.type === 'puzzleStarted' && evt.track === 'intermediate' ? current + 1 : undefined;
        default:
            return undefined;
    }
}
export function progressOf(state, id) {
    return state.progress[id] ?? 0;
}
export function isComplete(state, q) {
    return progressOf(state, q.id) >= q.target;
}
export function applyEvent(state, evt) {
    // The run only matters until `win_streak_3` is done; after that it stops
    // moving, so it stops causing saves.
    const runQuest = QUESTS.find((q) => q.id === 'win_streak_3');
    const runLive = runQuest !== undefined && !isComplete(state, runQuest);
    let run = state.run;
    if (runLive && evt.type === 'win')
        run += 1;
    if (runLive && evt.type === 'attemptBroken')
        run = 0;
    const progress = { ...state.progress };
    const completed = [];
    let changed = run !== state.run;
    for (const q of QUESTS) {
        const current = progressOf(state, q.id);
        if (current >= q.target)
            continue;
        const next = nextValue(q, current, evt, run);
        if (next === undefined)
            continue;
        const capped = Math.min(q.target, next);
        if (capped === current)
            continue;
        progress[q.id] = capped;
        changed = true;
        if (capped >= q.target)
            completed.push(q.id);
    }
    return changed
        ? { state: { ...state, progress, run }, completed, changed }
        : { state, completed, changed };
}
/** Completed, not yet paid — what the "!" dot and Claim buttons read. */
export function claimable(state) {
    return QUESTS.filter((q) => isComplete(state, q) && !state.claimed.includes(q.id));
}
/** Pay one quest: the new state and the coins, or null when it isn't claimable. */
export function claimQuest(state, id) {
    const q = QUESTS.find((d) => d.id === id);
    if (!q || !isComplete(state, q) || state.claimed.includes(id))
        return null;
    return { state: { ...state, claimed: [...state.claimed, id] }, coins: q.coins };
}
